import { Component } from '@theme/component';
import { catalogIdWithoutPrefix } from '@theme/custom.search-query';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { hoistSectionStylesheet, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Last count text from `#getResultsCount`, re-applied after each results morph
   * (which ships the count `<h4>` empty).
   * @type {string}
   */
  #lastResultsCount = '';

  /**
   * Term the results page should open on: set only when the typed term found nothing and its
   * catalog-ID retry did find something, so the "view all" paths follow the drawer.
   * @type {string | null}
   */
  #resultsPageTerm = null;

  /**
   * The count heading of the results on screen (typing state only). Read from the DOM, not refs:
   * refs re-sync after a morph, and only once the component's observer has attached.
   * @returns {Element | null}
   */
  get #resultsCount() {
    return this.refs.predictiveSearchResults.querySelector('[ref="resultsCount"]');
  }

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      // This module loads at low priority, so the modal can already be open, its event missed.
      if (this.closest('dialog')?.open) this.#handleDialogOpen();

      this.addEventListener('click', this.#handleModalClick, { signal });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Puts the server-rendered empty state in place. It fires in the frame that opens the dialog,
   * before that frame paints.
   */
  #handleDialogOpen = () => {
    const template = this.refs.predictiveSearchResults.querySelector(
      ':scope > template[data-empty-state]'
    );
    if (template instanceof HTMLTemplateElement) template.replaceWith(template.content);
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    // Horizontal arrows fall through to native text-caret movement, which the
    // browser already mirrors in RTL.
    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          const activatable = this.#currentItem?.querySelector('a, button');
          if (activatable instanceof HTMLElement) activatable.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.#resultsPageTerm ?? this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Sends the form to the results page on the term the drawer actually found results for, which is
   * the retried form of a catalog ID. Left alone otherwise, so the form submits natively and the
   * results page runs its own retry.
   * @param {SubmitEvent} event - The form's submit event.
   */
  onSubmit = (event) => {
    const searchTerm = this.#resultsPageTerm;

    if (!searchTerm || searchTerm === this.refs.searchInput.value) return;

    event.preventDefault();

    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', searchTerm);
    window.location.href = searchUrl.toString();
  };

  /**
   * Applies a clicked/entered autocomplete suggestion: fills the search input
   * with the suggested term, focuses it, and re-searches immediately.
   * Intentionally bypasses the `search()` debounce/inputType guard - this is
   * a discrete action, not a keystroke.
   * @param {Event} event - The click event from the suggestion button.
   */
  applySuggestion(event) {
    const target = event.target instanceof Element ? event.target.closest('[data-suggestion]') : null;
    if (!(target instanceof HTMLElement) || !target.dataset.suggestion) return;

    const suggestion = target.dataset.suggestion;

    this.refs.searchInput.value = suggestion;
    this.refs.searchInput.focus();
    this.#showResetButton();
    this.#currentIndex = -1;
    this.#getSearchResults(suggestion);
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;
    this.#resultsPageTerm = null;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   * @param {boolean} [isRetry] - True when this is already a catalog-ID retry.
   */
  async #getSearchResults(searchTerm, isRetry = false) {
    const { sectionId } = this.dataset;
    if (!sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();
    const retryTerm = isRetry ? null : catalogIdWithoutPrefix(searchTerm);

    // Only the first fetch shows the skeleton; later keystrokes keep the previous results up.
    // `[data-search-results]` exists only in the results markup, never in the empty state.
    if (!predictiveSearchResults.querySelector('[data-search-results]')) {
      this.#showSkeleton();
    }

    if (!retryTerm) this.#getResultsCount(searchTerm, abortController);

    sectionRenderer
      .getSectionHTML(sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) {
          // A newer keystroke's fetch immediately re-shows the skeleton, so
          // only clear it here if this call is still the active one.
          if (!abortController.signal.aborted) this.#hideSkeleton();
          return;
        }

        if (abortController.signal.aborted) return;

        // `[data-search-empty]` only exists on the drawer's no-results branch
        // (snippets/custom.predictive-search-no-results.liquid).
        const results = new DOMParser().parseFromString(resultsMarkup, 'text/html');
        const foundNothing = Boolean(results.querySelector('[data-search-empty]'));

        if (foundNothing && retryTerm) {
          this.#getSearchResults(retryTerm, true);
          return;
        }

        if (retryTerm) this.#getResultsCount(searchTerm, abortController);

        hoistSectionStylesheet(sectionId, results);
        morph(predictiveSearchResults, /** @type {Node} */ (results.body.firstChild));
        this.#hideSkeleton();

        if (foundNothing && isRetry) {
          // The retried term is the ID without its prefix, not what the shopper typed, so the
          // no-results line must not quote it back at them.
          const termElement = predictiveSearchResults.querySelector('.custom-search-drawer-no-results__term');
          if (termElement) termElement.textContent = this.refs.searchInput.value.trim();
        } else if (isRetry) {
          this.#resultsPageTerm = searchTerm;
        }

        // The morph above blanks the count <h4>, so re-apply the last-known
        // count; #getResultsCount overwrites it once its fetch resolves.
        const resultsCount = this.#resultsCount;
        if (resultsCount) {
          resultsCount.textContent = this.#lastResultsCount;
        }

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        this.#hideSkeleton();
        throw error;
      });
  }

  /**
   * Fetches the results count for a search term (via a second Section
   * Rendering API request against a tiny section exposing `search.results_count`,
   * since predictive search never returns a total) and writes it into
   * the count heading on screen.
   *
   * Shares the abort controller created in `#getSearchResults` - creating a
   * second one here would abort the *results* fetch instead (see
   * `#createAbortController`).
   * @param {string} searchTerm - The term to search for
   * @param {AbortController} abortController - The abort controller shared with `#getSearchResults`
   */
  #getResultsCount(searchTerm, abortController) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    // The drawer only renders products, so constrain the count to products -
    // otherwise articles/pages inflate `search.results_count` past what's shown.
    url.searchParams.set('type', 'product');

    sectionRenderer
      .getSectionHTML('custom-search-count', false, url)
      .then((countMarkup) => {
        if (abortController.signal.aborted) return;

        const countElement = new DOMParser().parseFromString(countMarkup, 'text/html').querySelector('.custom-search-count');

        this.#lastResultsCount = countElement?.textContent?.trim() ?? '';

        const resultsCount = this.#resultsCount;
        if (resultsCount) {
          resultsCount.textContent = this.#lastResultsCount;
        }
      })
      .catch(() => {
        // Cosmetic count - swallow the error but clear the stale count so the
        // results morph doesn't re-stamp the previous term's number.
        if (abortController.signal.aborted) return;
        this.#lastResultsCount = '';
        const resultsCount = this.#resultsCount;
        if (resultsCount) resultsCount.textContent = '';
      });
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  /**
   * Shows the first-fetch loading skeleton (see `custom.predictive-search-skeleton`)
   * by flagging the component as loading and marking the results container busy.
   */
  #showSkeleton() {
    this.classList.add('predictive-search--loading');
    this.refs.predictiveSearchResults.setAttribute('aria-busy', 'true');
  }

  /**
   * Hides the loading skeleton shown by `#showSkeleton`.
   */
  #hideSkeleton() {
    this.classList.remove('predictive-search--loading');
    this.refs.predictiveSearchResults.removeAttribute('aria-busy');
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    try {
      const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
      const parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(emptySectionMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section');

      if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

      /** This needs to be awaited and not .then so the DOM is already morphed
       * when #closeResults is called and therefore the height is animated */
      if (abortController.signal.aborted) return;

      // The section's own wrapper, so it holds the response's stylesheet.
      hoistSectionStylesheet(emptySectionId, parsedEmptySectionMarkup);
      morph(predictiveSearchResults, parsedEmptySectionMarkup);
      this.#hideSkeleton();
      this.#resetScrollPositions();
    } catch (error) {
      // Otherwise the skeleton outlives the failed fetch and aria-busy sticks. A newer call
      // re-shows the skeleton, so only clear it if this call is still the active one.
      if (abortController.signal.aborted) return;
      this.#hideSkeleton();
      throw error;
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
